import React, { useCallback, useMemo } from 'react';
import { useGoogleMap } from '@react-google-maps/api';

import { useNavigation } from '@atom/hooks/useNavigation';
import { FormattedMarker, MapParams } from '@atom/types/map';
import { formatMapData } from '@atom/utilities/mapUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { useGoogleMapsStateValue } from './hooks/googleMapsStateHook';
import MapShapeMemo from './markers/MapShapeMemo';

enum View {
  ASSET = 'asset',
  USER = 'user',
  WORK_ORDER = 'workOrder',
  WORK_ORDERS = 'workOrders',
  LIST = 'list',
}

const NOOP = () => {};

type Props = {
  data: unknown;
  // autoActive can only be used when there is a single marker on the map.
  // It denotes the id of the item that should be auto set to active on load.
  // Additionally it will remove the icon onClick
  autoActiveId?: string;
  // Optional onClick, will replace current param setting logic is passed
  onClick?: (id: string) => void;
  // By default the map interacts with center, zoom, id, searchPoint, and searchTerm
  // If you would like to instead store these in component state, you must pass in mapParams
  // and updateMapParams props. This will also need to be passed into <Map />
  mapParams?: MapParams;
  updateMapParams?: (mapParams: MapParams) => void;
};

const MapMarkers = ({
  data,
  autoActiveId = '',
  onClick,
  mapParams,
  updateMapParams,
}: Props) => {
  const map = useGoogleMap();
  const { grab } = useGoogleMapsStateValue();

  const [{ id: paramActiveId, hover: paramHoverId }, { updateSearchParams }] =
    useNavigation();

  const disabledIdsStr = (mapParams?.disabledIds || []).join(',');
  const activeIdsStr = isNilOrEmpty(updateMapParams)
    ? paramActiveId || ''
    : (mapParams?.ids || []).join(',');
  const hoverId = isNilOrEmpty(updateMapParams)
    ? paramHoverId
    : mapParams?.hoverId;
  const autoActive = !isNilOrEmpty(autoActiveId);
  const clickable = !grab;

  const sharedProps = useMemo(
    () => ({
      activeIds: autoActive ? autoActiveId : activeIdsStr,
      disabledIds: disabledIdsStr,
      hoverId,
      clickable,
    }),
    [
      autoActive,
      autoActiveId,
      activeIdsStr,
      disabledIdsStr,
      hoverId,
      clickable,
    ],
  );

  const mapData = useMemo(
    () => formatMapData(sharedProps, data),
    [sharedProps, data],
  );

  const onIconClick = useCallback(
    (view: View, id: string) => {
      const ids = id.split(',');
      const disabledIds = disabledIdsStr ? disabledIdsStr.split(',') : [];
      const allDisabled =
        ids.length > 0 && ids.every(item => disabledIds.includes(item));

      if (allDisabled) {
        return;
      }

      if (onClick || updateMapParams) {
        onClick?.(id);
        return;
      }

      if (!autoActive) {
        updateSearchParams({
          view,
          id,
          zoom: map.getZoom(),
          center: map.getCenter().toUrlValue(),
          hover: null,
        });
      }
    },
    [
      disabledIdsStr,
      onClick,
      updateMapParams,
      autoActive,
      updateSearchParams,
      map,
    ],
  );

  const onClusterClick = useCallback(
    (cluster: FormattedMarker) => {
      const { position } = cluster;
      map.setZoom(map.getZoom() + 3);
      map.setCenter(position);
    },
    [map],
  );

  const onAssetClick = useCallback(
    (marker: FormattedMarker) => onIconClick(View.ASSET, marker.id),
    [onIconClick],
  );
  const onWorkOrderClick = useCallback(
    (marker: FormattedMarker) => onIconClick(View.WORK_ORDER, marker.id),
    [onIconClick],
  );
  const onUserClick = useCallback(
    (marker: FormattedMarker) => onIconClick(View.USER, marker.id),
    [onIconClick],
  );
  const onAssetGroupClick = useCallback(
    (marker: FormattedMarker) => onIconClick(View.LIST, marker.id),
    [onIconClick],
  );
  const onWorkOrderGroupClick = useCallback(
    (marker: FormattedMarker) => onIconClick(View.WORK_ORDERS, marker.id),
    [onIconClick],
  );

  const {
    assets,
    clusters,
    workOrders,
    users,
    assetGroups,
    workOrderGroups,
    locations,
  } = mapData;

  return (
    <div>
      {assets.map(asset => (
        <MapShapeMemo marker={asset} key={asset.id} onClick={onAssetClick} />
      ))}
      {clusters.map(cluster => (
        <MapShapeMemo
          marker={cluster}
          key={cluster.id}
          onClick={onClusterClick}
        />
      ))}
      {workOrders.map(workOrder => (
        <MapShapeMemo
          marker={workOrder}
          key={workOrder.id}
          onClick={onWorkOrderClick}
        />
      ))}
      {users.map(user => (
        <MapShapeMemo marker={user} key={user.id} onClick={onUserClick} />
      ))}
      {assetGroups.map(assetGroup => (
        <MapShapeMemo
          marker={assetGroup}
          key={assetGroup.id}
          onClick={onAssetGroupClick}
        />
      ))}
      {workOrderGroups.map(workOrderGroup => (
        <MapShapeMemo
          marker={workOrderGroup}
          key={workOrderGroup.id}
          onClick={onWorkOrderGroupClick}
        />
      ))}
      {locations.map(location => (
        <MapShapeMemo marker={location} key={location.id} onClick={NOOP} />
      ))}
    </div>
  );
};

export default React.memo(MapMarkers);
